import { Injectable, inject } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppState } from '../../store';
import { isAdmin, isCourseAdmin, customRoles, selectAllowedCourses } from '../../store/selectors';
import { customRole } from '../../models/user-permissions.model';
import {PermissionKeys} from './permissions-key.enum';
export type EffectivePermissions = Record<string, boolean>;

@Injectable({ providedIn: 'root' })
export class UserPermissionsService {
  private store = inject<Store<AppState>>(Store);

  private effectivePermissions$ = new BehaviorSubject<EffectivePermissions>({});
  private allowedCourses: string[] = []; 
  private customRolesList: customRole = null;
  private permissions: PermissionKeys[] = [];

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);
  constructor() {
    this.buildPermissionsFromStore();


    this.store.pipe(select(selectAllowedCourses)).subscribe(courses => {
      this.allowedCourses = courses || [];
    });
  }

  // ✅ Build permissions reactively from store
  private buildPermissionsFromStore() {
    combineLatest([
      this.store.pipe(select(isAdmin)),
      this.store.pipe(select(isCourseAdmin)),
      this.store.pipe(select(customRoles))
    ])
      .pipe(
        map(([isAdmin, isCourseAdmin, customRoles]) => {
          const effective: EffectivePermissions = {};

          // Legacy roles
          if (isAdmin) {
            effective['all'] = true;
            return effective;
          }
          if (isCourseAdmin) {
            effective['course.manage'] = true;
          }

          // Custom roles
          if (customRoles) {
            
              this.customRolesList = customRoles;
              this.applyRolePermissions(customRoles, effective);
            
          }

          return effective;
        })
      )
      .subscribe(effective => {
        this.effectivePermissions$.next(effective);
      });
  }

  // ✅ Apply custom role permissions recursively
  private applyRolePermissions(role: customRole, effective: EffectivePermissions) {
    const traverse = (prefix: string, obj: any): boolean => {
      let hasTrue = false;
  
      for (const [key, value] of Object.entries(obj)) {
        const newKey = prefix ? `${prefix}.${key}` : key;
  
        if (typeof value === 'boolean') {
          effective[newKey] = value || effective[newKey] || false;
          if (value) hasTrue = true;
        } else if (typeof value === 'object') {
          const childHasTrue = traverse(newKey, value);
          if (childHasTrue) {
            hasTrue = true;
          }
        }
      }
  
      // ✅ if this node is an object and has at least one true child, mark the parent as true
      if (prefix && hasTrue) {
        effective[prefix] = true;
      } else if (prefix && effective[prefix] === undefined) {
        // ensure parent exists in map even if false
        effective[prefix] = false;
      }
  
      return hasTrue;
    };
  
    traverse('', role.permissions);
  }

  // ✅ Observable for reactive templates
  get permissions$() {
    return this.effectivePermissions$.asObservable();
  }

  // ✅ Simple sync getter
  can(permissionKey: string): boolean {
    const perms = this.effectivePermissions$.getValue();
    return perms['all'] || perms[permissionKey] || false;
  }

  // ✅ Synchronous check if a course is allowed
  haveCourseAccess(courseId: string): boolean {

    if (this.can('all') || this.can('editProductAllowed')) {
      return true;
    }

    if (this.allowedCourses.includes(courseId)) {
      return true;
    }

    // custom roles store allowed courses on the role object itself
    if (this.customRolesList?.allowedCourses?.includes(courseId)) {
      return true;
    }

    return false;
  }

  isCustomRoleUser(): boolean {
    return this.customRolesList !== null;
  }

  redirectUserAccordingToPermissions() {
    if(this.can('all') || this.can(PermissionKeys.AdminSettingsGeneral)) {
      return '/admin/settings/school-settings';
    }
    if(this.can(PermissionKeys.AdminSettingsMyAccount)) {
      return '/admin/settings/school-settings/account';
    }
    if(this.can(PermissionKeys.AdminSettingsIntegrations)) {
      return '/admin/settings/school-settings/integrations';
    }
    if(this.can(PermissionKeys.AdminSettingsDomainName)) {
      return '/admin/settings/school-settings/domain-name';
    }
    if(this.can(PermissionKeys.AdminSettingsBrandingTheme)) {
      return '/admin/settings/school-settings/branding';
    }
    if(this.can(PermissionKeys.AdminSettingsNavigationsSocials)) {
      return '/admin/settings/school-settings/navigation';
    }
    if(this.can(PermissionKeys.AdminSettingsAI)) {
      return '/admin/settings/school-settings/ai-assistant';
    }
    if(this.can(PermissionKeys.AdminSettingsEmailTemplates)) {
      return '/admin/settings/school-settings/email-template';
    }
    if(this.can(PermissionKeys.AdminSettingsEmailMarketing)) {
      return '/admin/settings/email-marketing';
    }
    if(this.can(PermissionKeys.AdminSettingsTeamMembers)) {
      return '/admin/settings/manage-team-members';
    }
    if(this.can(PermissionKeys.AdminSettingsStudentsManage)) {
      return '/admin/settings/manage-students';
    }
    if(this.can(PermissionKeys.AdminSettingsStudentsProgress)) {
      return '/admin/settings/student-progress';
    }
    if(this.can(PermissionKeys.AdminSettingsQuestionBank)) {
      return '/admin/settings/question-bank';
    }
    if(this.can(PermissionKeys.AdminSettingsReviewAssessments)) {
      return '/admin/settings/review-assessments';
    }
    if(this.can(PermissionKeys.AdminSettingsManageAssignments)) {
      return '/admin/settings/manage-assignments';
    }
    if(this.can(PermissionKeys.AdminSettingsBulkEmails)) {
      return '/admin/settings/send-bulk-email';
    }
    if(this.can(PermissionKeys.AdminSettingsCourseBundles)) {
      return '/admin/settings/edit-bundles';
    }
    if(this.can(PermissionKeys.AdminSettingsSubscriptionCoupons)) {
      return '/admin/settings/subscription-coupons';
    }
    if(this.can(PermissionKeys.AdminSettingsAffiliateProgram)) {
      return '/admin/settings/affiliate-program';
    }
    

    
 
  }
}
